import {
  Card,
  CardHeader,
  CardBody,
  Label,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
} from "reactstrap";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import AdminHeader from "components/Headers/AdminHeader";
import {
  REGEX,
  MAX_FILE_SIZE,
  SELECT_CUSTOM_STYLES,
} from "constants/CommonConstants";

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
  { value: "Prefer not to say", label: "Prefer not to say" },
];

const skillsOptions = [
  { label: "Cable Jointing", value: "Cable Jointing" },
  { label: "Substation Maintenance", value: "Substation Maintenance" },
  { label: "HVAC Maintenance", value: "HVAC Maintenance" },
  { label: "Electrical Safety", value: "Electrical Safety" },
];

// Static instructor data for prefilling
const staticInstructorData = [
  {
    id: 1,
    firstName: "Rahul",
    lastName: "Sharma",
    email: "rahul.sharma@training.com",
    phone: "9876543210",
    experienceYears: 8,
    skills: ["Cable Jointing", "Substation Maintenance"],
    profilePhoto: "rahul.png",
    gender: "Male",
    role: "Instructor",
    accountStatus: "ACTIVE",
    assignedCourses: ["Electrical Safety", "Power Systems"],
    certifications: [],
    supportingDocuments: [],
  },
  {
    id: 2,
    fullName: "Priya Verma",
    email: "priya.verma@training.com",
    phone: "9123456789",
    experienceYears: 5,
    skills: ["HVAC Maintenance"],
    profilePhoto: "priya.png",
    gender: "Female",
    role: "Instructor",
    accountStatus: "ACTIVE",
    assignedCourses: [],
    certifications: [],
    supportingDocuments: [],
  },
  {
    id: 3,
    fullName: "Amit Kulkarni",
    email: "amit.kulkarni@training.com",
    phone: "9988776655",
    experienceYears: 12,
    skills: ["Cable Jointing", "Electrical Safety"],
    profilePhoto: "",
    gender: "Prefer not to say",
    role: "Instructor",
    accountStatus: "INACTIVE",
    assignedCourses: ["Industrial Wiring"],
    certifications: [],
    supportingDocuments: [],
  },
];

const initialFields = {
  firstName: {
    value: "",
    error: "",
  },
  lastName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  phone: {
    value: "",
    error: "",
  },
  experienceYears: {
    value: "",
    error: "",
  },
  gender: {
    value: "",
    error: "",
  },
  skills: {
    value: [],
    error: "",
    isChanged: false,
  },
  accountStatus: {
    value: "ACTIVE",
    error: "",
  },
};

function AddInstructor() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || "add";
  const [formData, setFormData] = useState(initialFields);

  // Prefill form when in edit mode using static data
  useEffect(() => {
    if (mode === "edit") {
      // Always use the first instructor's data from static data
      const instructor = staticInstructorData[0];

      setFormData({
        firstName: {
          value: instructor.firstName || "",
          error: "",
        },
        lastName: {
          value: instructor.lastName || "",
          error: "",
        },
        email: {
          value: instructor.email || "",
          error: "",
        },
        phone: {
          value: instructor.phone || "",
          error: "",
        },
        experienceYears: {
          value: instructor.experienceYears || "",
          error: "",
        },
        gender: {
          value:
            instructor.gender ||
            null,
          error: "",
        },
        skills: {
          value: (instructor.skills || []),
          error: "",
          isChanged: false,
        },
        certifications: {
          value: instructor.certifications || [],
          error: "",
          isChanged: false,
        },
        accountStatus: {
          value: instructor.accountStatus || "Active",
          error: "",
        },
      });
    }
  }, [mode]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const getValidationError = (name, value, allValues = {}) => {
    const isEmpty =
      value === "" ||
      value === null ||
      (Array.isArray(value) && value.length === 0);

    switch (name) {
      // Basic Information
      case "firstName":
        if (isEmpty) return "Please enter first name";
        else if (value.trim().length < 2)
          return "First name must be at least 2 characters";
        else if (!REGEX.ONLY_ALPHABETS_REGEX.test(value))
          return "Only alphabets and spaces are allowed";
        return "";

      case "lastName":
        if (isEmpty) return "Please enter last name";
        else if (value.trim().length < 2)
          return "Last name must be at least 2 characters";
        else if (!REGEX.ONLY_ALPHABETS_REGEX.test(value))
          return "Only alphabets and spaces are allowed";
        return "";

      case "email":
        if (isEmpty) return "Please enter email";
        else if (!value && !allValues?.phone) return "Email is required";
        else if (value && !REGEX.EMAIL_REGEX.test(value))
          return "Enter a valid email address";
        return "";

      case "phone":
        if (!value && !allValues?.email) return "Mobile number is required";
        else if (value && !REGEX.PHONE_NO_REGEX.test(value))
          return "Mobile number must contain 10 digits only";
        return "";

      case "experienceYears":
        if (isEmpty) return "Please enter years of experience";
        else if (Number(value) < 0) return "Experience should be more than 0";
        else if (Number(value) > 50) return "Experience should be less than 50";
        return "";

      // Professional Details
      case "skills":
        return isEmpty ? "Please select at least one skill" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChanged: true,
      },
    }));
  };

  return (
    <>
      <AdminHeader parentName="Trainers" />
      <Container className="mt--6" fluid>
        <Card className="mb-4">
          <CardHeader className="d-flex align-items-center ">
            <i
              className="fa fa-chevron-left mr-2 cursor"
              onClick={() =>
                navigate(
                  mode === "edit"
                    ? role === "SUPER_ADMIN"
                      ? "/super-admin/instructor/f9mb3/details"
                      : "/admin/instructor/f9mb3/details"
                    : role === "SUPER_ADMIN"
                      ? "/super-admin/instructor"
                      : "/admin/instructor",
                )
              }
            />
            <h3 className="mb-0">
              {mode === "edit" ? "Edit Trainer" : "Add Trainer"}
            </h3>
          </CardHeader>

          <CardBody>
            {/* Basic Information */}
            <Row>
              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">
                    First Name <span className="text-danger"> *</span>
                  </Label>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName.value}
                    placeholder="Enter first name"
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    className={formData.firstName.error ? "is-invalid" : ""}
                  />
                  {formData.firstName.error && (
                    <small className="invalid-feedback d-block">
                      {formData.firstName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">
                    Last Name <span className="text-danger"> *</span>
                  </Label>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName.value}
                    placeholder="Enter last name"
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    className={formData.lastName.error ? "is-invalid" : ""}
                  />
                  {formData.lastName.error && (
                    <small className="invalid-feedback d-block">
                      {formData.lastName.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">
                    Email<span className="text-danger"> *</span>
                  </Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email.value}
                    placeholder="Enter email"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    className={formData.email.error ? "is-invalid" : ""}
                  />
                  {formData.email.error && (
                    <small className="invalid-feedback d-block">
                      {formData.email.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">
                    Mobile Number <span className="text-danger"> *</span>
                  </Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone.value}
                    placeholder="Enter mobile number"
                    maxLength={10}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    className={formData.phone.error ? "is-invalid" : ""}
                  />
                  {formData.phone.error && (
                    <small className="invalid-feedback d-block">
                      {formData.phone.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">Gender</Label>

                  <Select
                    name="gender"
                    options={genderOptions}
                    styles={SELECT_CUSTOM_STYLES}
                    value={
                      genderOptions.find(
                        (opt) => opt.value === formData.gender.value,
                      ) || null
                    }
                    className={formData.gender.error ? "is-invalid" : ""}
                    onBlur={() => handleOnBlur("gender", formData.gender.value)}
                    onChange={(selected) =>
                      handleInputChange("gender", selected)
                    }
                    isClearable
                  />

                  {formData.gender.error && (
                    <small className="invalid-feedback d-block">
                      {formData.gender.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">
                    Prior Years of Experience{" "}
                    <span className="text-danger"> *</span>
                  </Label>
                  <Input
                    type="text"
                    name="experienceYears"
                    value={formData.experienceYears.value}
                    placeholder="Enter year of Experience  eg: 7 yrs 3 months "
                    minLength={0}
                    maxLength={2}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    className={
                      formData.experienceYears.error ? "is-invalid" : ""
                    }
                  />
                  {formData.experienceYears.error && (
                    <small className="invalid-feedback d-block">
                      {formData.experienceYears.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {/* Professional Details */}
            <Row>
              <Col md="4">
                <FormGroup>
                  <Label className="form-control-label">Skills</Label>

                  <Select
                    isMulti
                    options={skillsOptions}
                    styles={SELECT_CUSTOM_STYLES}
                    value={skillsOptions.filter((opt) =>
                      formData.skills.value.includes(opt.value),
                    )}
                    className={formData.skills.error ? "is-invalid" : ""}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    onChange={(selected) =>
                      handleChange(
                        "skills",
                        selected.map((item) => item.value),
                      )
                    }
                  />

                  {formData.skills.error && (
                    <small className="invalid-feedback d-block">
                      {formData.skills.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4" sm="12">
                <FormGroup>
                  <Label className="form-control-label">
                    Status <span className="text-danger">*</span>
                  </Label>

                  <div className="d-flex mt-2">
                    <FormGroup check className="mr-4">
                      <Label check>
                        <Input
                          type="radio"
                          name="accountStatus"
                          className="cursor"
                          checked={formData.accountStatus.value === "ACTIVE"}
                          onChange={() =>
                            handleChange("accountStatus", "ACTIVE")
                          }
                        />
                        Active
                      </Label>
                    </FormGroup>

                    <FormGroup check>
                      <Label check>
                        <Input
                          type="radio"
                          name="accountStatus"
                          className="cursor"
                          checked={formData.accountStatus.value === "INACTIVE"}
                          onChange={() =>
                            handleChange("accountStatus", "INACTIVE")
                          }
                        />
                        Inactive
                      </Label>
                    </FormGroup>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* Action Buttons */}
            <Row className="mt-4">
              <Col className="text-right">
                <button
                  className="btn btn-secondary mr-2"
                  onClick={() =>
                    navigate(
                      mode === "edit"
                        ? role === "SUPER_ADMIN"
                          ? "/super-admin/instructor/f9mb3/details"
                          : "/admin/instructor/f9mb3/details"
                        : role === "SUPER_ADMIN"
                          ? "/super-admin/instructor"
                          : "/admin/instructor",
                    )
                  }
                >
                  Cancel
                </button>
                <button
                  className="btn btn-deep-blue"
                  color="primary"
                  type="submit"
                  onClick={() =>
                    navigate(
                      mode === "edit"
                        ? role === "SUPER_ADMIN"
                          ? "/super-admin/instructor/f9mb3/details"
                          : "/admin/instructor/f9mb3/details"
                        : role === "SUPER_ADMIN"
                          ? "/super-admin/instructor"
                          : "/admin/instructor",
                    )
                  }
                >
                  Save
                </button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
    </>
  );
}

export default AddInstructor;
